import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { MarkdownPreview } from '@/components/features/markdown-preview'
import { SidebarErrorState } from '@/components/layout/sidebar/SidebarErrorState'
import { fetchWithAuth } from '@/lib/api/core'
import { testIds } from '@/lib/test-ids'

type DriveFileContentResponse = {
  content?: string
  name?: string
  mimeType?: string
  error?: string
}

export function DriveNativeTextPreview({
  fileId,
  organizationId,
}: {
  fileId: string
  organizationId?: string
}) {
  const [content, setContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      setContent(null)
      try {
        const params = organizationId
          ? `?organizationId=${encodeURIComponent(organizationId)}`
          : ''
        const res = await fetchWithAuth(
          `/api/google-drive/files/${encodeURIComponent(fileId)}/content${params}`,
        )
        const body = (await res.json().catch(() => ({}))) as DriveFileContentResponse
        if (cancelled) return
        if (!res.ok) {
          throw new Error(
            typeof body.error === 'string' ? body.error : `HTTP ${res.status}`,
          )
        }
        setContent(typeof body.content === 'string' ? body.content : '')
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load preview')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [fileId, organizationId])

  if (loading) {
    return (
      <div
        className="flex h-full items-center gap-2 px-4 py-4 text-sm text-muted-foreground"
        data-testid={testIds.driveFolderView.previewNativeText}
      >
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        Loading preview
      </div>
    )
  }

  if (error) {
    return (
      <div className="h-full overflow-y-auto" data-testid={testIds.driveFolderView.previewNativeText}>
        <SidebarErrorState message={error} />
      </div>
    )
  }

  return (
    <div
      className="h-full min-h-0 overflow-y-auto"
      data-testid={testIds.driveFolderView.previewNativeText}
    >
      <MarkdownPreview
        content={content ?? ''}
        className="max-w-none"
        readingColumnPadding="panel"
      />
    </div>
  )
}
